import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import CENHomeArrowRightFirst from "@tCentro/Icons/CENHomeArrowRightFirst";
import CENHomeArrowRightSecond from "@tCentro/Icons/CENHomeArrowRightSecond";
import CENHomeCardCheck from "@tCentro/Icons/CENHomeCardCheck";
import parse from "html-react-parser";
import { connect, ResolveThunks } from "react-redux";

import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";
import Img from "@insite/mobius/Img";
import Clickable from "@insite/mobius/Clickable";

interface BenefitsList {
    fields: {
        textarea: string;
    };
}

const enum fields {
    background = "background",
    desktop_image = "desktop_image",
    tablet_image = "tablet_image",
    mobile_image = "mobile_image",
    backgroundColor = "backgroundColor",
    cardHeading = "cardHeading",
    cardImg = "cardImg",
    cardDescription = "cardDescription",
    cardLinkText = "cardLinkText",
    cardLinkURL = "cardLinkURL",
    aboutTitle = "aboutTitle",
    aboutDescription = "aboutDescription",
    aboutLinkText = "aboutLinkText",
    aboutLinkURL = "aboutLinkURL",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.desktop_image]: string;
        [fields.tablet_image]: string;
        [fields.mobile_image]: string;
        [fields.cardHeading]: string;
        [fields.backgroundColor]: string;
        [fields.cardImg]: string;
        [fields.cardDescription]: string;
        [fields.cardLinkText]: string;
        [fields.cardLinkURL]: LinkFieldValue;
        [fields.aboutTitle]: string;
        [fields.aboutDescription]: string;
        [fields.aboutLinkText]: string;
        [fields.aboutLinkURL]: LinkFieldValue;
    };
}

type Props = OwnProps;

export interface HomePageBannerNewStyles {
    mainContainer?: InjectableCss;
    aboutContainer?: InjectableCss;
    cardHeadingStyle?: InjectableCss;
    cardInfoStyle?: InjectableCss;
    cardDescriptionStyle?: InjectableCss;
    cardImageStyle?: InjectableCss;
    cardButtonStyle?: InjectableCss;
    aboutHeadingStyle?: InjectableCss;
    aboutDescriptionStyle?: InjectableCss;
    aboutButtonStyle?: InjectableCss;
}

export const homePageBannerNewStyles: HomePageBannerNewStyles = {
    mainContainer: {
        css: css`
            display: flex;
            width: 100%;
            max-width: 1800px;
            margin: 0 auto;
            justify-content: center;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 16px;
                        padding-left: 20px;
                        flex-direction: column;
                        margin-bottom: 32px;
                    `,
                    css`
                        padding-right: 16px;
                        padding-left: 20px;
                        flex-direction: column;
                        margin-bottom: 40px;
                    `,
                    css`
                        padding-right: 16px;
                        padding-left: 20px;
                        flex-direction: column;
                        margin-bottom: 40px;
                    `,
                    css`
                        flex-direction: row;
                        padding-right: 64px;
                        padding-left: 64px;
                        margin-bottom: 64px;
                    `,
                    css`
                        flex-direction: row;
                        padding-right: 64px;
                        padding-left: 64px;
                        margin-bottom: 64px;
                    `,
                ])}
        `,
    },
    cardInfoStyle: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    aboutContainer: {
        css: css`
            display: flex;
            // max-width: 700px;
            width: 100%;
            flex-direction: column;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-left: 64px;
                        padding-right: 68px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    cardHeadingStyle: {
        css: css`
            font-family: "Roboto Condensed";
            font-weight: 600 !important;
            font-size: 32px;
            line-height: 1 !important;
            color: ${getColor(COLORS.white)};
            margin-bottom: 10px;
            @media all and (max-width: 767px) {
                font-size: 22px;
            }
        `,
    },
    cardDescriptionStyle: {
        css: css`
            font-family: "Roboto, sans-serif";
            font-size: 18px;
            line-height: 28px;
            margin-bottom: 18px;
            @media all and (max-width: 767px) {
                font-size: 12px !important;
                line-height: 16px;
            }
        `,
    },
    cardImageStyle: {
        css: css`
            margin: 0;
            height: auto;
            align-self: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            align-self: center;
                            width: 100%;
                            max-width: 100%;
                        `,
                        css`
                            align-self: center;
                            width: 100%;
                            max-width: 100%;
                        `,
                        css`
                            max-width: 167px;
                        `,
                        css`
                            max-width: 167px;
                        `,
                        css`
                            max-width: 167px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cardButtonStyle: {
        css: css`
            font-family: "Roboto Condensed";
            font-weight: 600 !important;
            font-size: 16px !important;
            background-color: #fbd51e;
            border-radius: 4px;
            text-transform: uppercase;
            display: inline-block;
            padding: 8px 16px;
            span {
                color: ${getColor(COLORS.black)} !important;
            }
            @media all and (max-width: 767px) {
                text-align: center;
                display: block;
            }
        `,
    },
    aboutHeadingStyle: {
        css: css`
            font-family: "Roboto Condensed";
            font-weight: 600 !important;
            font-size: 32px;
            line-height: 1 !important;
            color: #2b5197;
            margin-bottom: 10px;
            @media all and (max-width: 767px) {
                font-size: 24px;
            }
        `,
    },
    aboutDescriptionStyle: {
        css: css`
            font-family: "Roboto, sans-serif";
            font-size: 14px;
            line-height: 24px;
        `,
    },
    aboutButtonStyle: {
        css: css`
            font-family: "Roboto Condensed";
            font-weight: 600 !important;
            font-size: 16px !important;
            background-color: #2b5197;
            border-radius: 4px;
            text-transform: uppercase;
            display: inline-block;
            padding: 8px 16px;
            margin-top: 16px;
            align-self: flex-start;
            span {
                color: ${getColor(COLORS.white)} !important;
            }
        `,
    },
};
const styles = homePageBannerNewStyles;

const NISAbout: FC<Props> = ({ fields }) => {
    const desktopBackgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.desktop_image).url}) !important;
           background-size: 100% 100%;
           margin: 0;
           background-repeat:no-repeat;
           `
            : `background-color: ${fields.backgroundColor};`;
    const tabletBackgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.tablet_image).url}) !important;
           background-size: 100% 100%;
           margin: 0;
           background-repeat:no-repeat;
           `
            : `background-color: ${fields.backgroundColor};`;
    const mobileBackgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.mobile_image).url}) !important;
           background-size: 100% 100%;
           margin: 0;
           background-repeat:no-repeat;
           `
            : `background-color: ${fields.backgroundColor};`;

    const container = {
        css: css`
            max-width: 500px;
            width: 100%;
            display: flex;
            padding: 24px;
            gap: 16px;
            align-self: flex-start;
            border-radius: 8px;
            outline: none !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        ${mobileBackgroundStyles};
                        flex-direction: column;
                        max-width: 100%;
                        margin: 0 auto;
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        ${mobileBackgroundStyles};
                        flex-direction: column;
                        max-width: 100%;
                        margin: 0 auto;
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        max-width: 610px;
                        ${tabletBackgroundStyles};
                        align-self: center;
                    `,
                    css`
                        ${desktopBackgroundStyles};
                    `,
                    css`
                        ${desktopBackgroundStyles};
                    `,
                ])}
        `,
    };

    const { url: cardLinkURL } = useGetLink(fields.cardLinkURL);
    const { url: aboutLinkURL } = useGetLink(fields.aboutLinkURL);

    return (
        <StyledWrapper {...styles.mainContainer}>
            <Clickable href={cardLinkURL} {...container}>
                <Img src={fields.cardImg} altText={fields.cardHeading} {...styles.cardImageStyle} />
                <StyledWrapper {...styles.cardInfoStyle}>
                    <Typography variant="h3" {...styles.cardHeadingStyle}>
                        {fields.cardHeading}
                    </Typography>
                    <StyledWrapper {...styles.cardDescriptionStyle}>
                        {parse(transformContentHtml(fields.cardDescription), parserOptions)}
                    </StyledWrapper>
                    <div>
                        <Link href={cardLinkURL} {...styles.cardButtonStyle}>
                            {fields.cardLinkText}
                        </Link>
                    </div>
                </StyledWrapper>
            </Clickable>
            <StyledWrapper {...styles.aboutContainer}>
                <Typography as="h3" {...styles.aboutHeadingStyle}>
                    {fields.aboutTitle}
                </Typography>
                <StyledWrapper {...styles.aboutDescriptionStyle}>
                    {parse(transformContentHtml(fields.aboutDescription), parserOptions)}
                </StyledWrapper>
                <Link href={aboutLinkURL} {...styles.aboutButtonStyle}>
                    {fields.aboutLinkText}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: NISAbout,
    definition: {
        group: "Common",
        displayName: "NISAbout",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.desktop_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Desktop_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.tablet_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Tablet Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.mobile_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Mobile Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Contextual",
                name: fields.cardImg,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Card_Image",
            },
            {
                name: fields.cardHeading,
                editorTemplate: "TextField",
                displayName: "Card Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.cardDescription,
                editorTemplate: "RichTextField",
                displayName: "Card Description",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.cardLinkText,
                editorTemplate: "TextField",
                displayName: "Card Link Name",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "Contextual",
                name: fields.cardLinkURL,
                displayName: "Card Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.aboutTitle,
                editorTemplate: "TextField",
                displayName: "About Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.aboutDescription,
                editorTemplate: "RichTextField",
                displayName: "About Description",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.aboutLinkText,
                editorTemplate: "TextField",
                displayName: "About Link Name",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "Contextual",
                name: fields.aboutLinkURL,
                displayName: "About Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};
export default widgetModule;
